/** @jsxImportSource theme-ui @jsxRuntime classic */
import React, { Fragment, useMemo, useState, useCallback } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import {
  Spinner,
  Flex,
  Section,
  useCurator,
  SocialCarousel,
  ModalSocialCarousel,
  ThemeProvider,
  getThemeByName,
  Modal,
  SxStyleProp,
} from '@tishman/components';
import { format as formatDate } from 'date-fns';

import ErrorMessage from './ErrorMessage';
import ShareYourExperienceBlockHeader, {
  ShareYourExperienceBlockHeaderProps,
} from './ShareYourExperienceBlockHeader';

import type { ComponentPropsWithoutRef } from 'react';
import type {
  CuratorFeedName,
  SocialCarouselCardProps,
  ModalSocialCarouselCardProps,
} from '@tishman/components';

type CardProps = SocialCarouselCardProps & ModalSocialCarouselCardProps;

export interface ShareYourExperienceCarouselProps {
  feedName?: CuratorFeedName;
  desktopCardsPaddingLeft?: string;
  mobileContainerFullWidth?: boolean;
  carouselVariant?: 'default' | 'private-events';
}

const ShareYourExperienceCarousel = ({
  feedName,
  desktopCardsPaddingLeft,
  mobileContainerFullWidth,
  carouselVariant,
}: ShareYourExperienceCarouselProps): React.JSX.Element => {
  const useCuratorResult = useCurator({ feedName });

  const cards = useMemo(() => {
    if (!useCuratorResult.data) return null;

    return useCuratorResult.data.reduce<CardProps[]>((acc, d) => {
      if (!d) throw new Error('Expected valid data');
      if (!d.id) {
        console.warn('Expected valid Curator post id');
        return acc;
      }
      if (!d.text) {
        console.warn(`Expected valid caption, Curator post id: ${d.id}`);
        return acc;
      }
      if (!d.image) {
        console.warn(`Expected valid image, Curator post id: ${d.id}`);
        return acc;
      }
      if (!d.source_created_at) {
        console.warn(`Expected valid creation time, Curator post id: ${d.id}`);
        return acc;
      }

      acc.push({
        id: `${d.id}`,
        caption: d.text,
        image: d.image,
        url: d.url,
        authorImage: d.user_image,
        authorScreenName: d.user_screen_name,
        creation_timestamp: formatDate(
          new Date(d.source_created_at),
          'MM/dd/yyyy',
        ),
      });
      return acc;
    }, []);
  }, [useCuratorResult.data]);

  const [modalId, openModal] = useState<string | null>(null);

  const initialModalCardIndex = cards?.findIndex((card) => card.id === modalId);

  const closeModal = useCallback(() => openModal(null), []);

  return cards ? (
    <Fragment>
      <SocialCarousel
        cards={cards}
        carouselVariant={carouselVariant}
        desktopCardsPaddingLeft={desktopCardsPaddingLeft}
        mobileContainerFullWidth={mobileContainerFullWidth}
        onActiveCardChange={openModal}
      />
      <ThemeProvider theme={getThemeByName('Rock Center')}>
        <Modal
          id="share-your-experience-block"
          isOpen={modalId !== null}
          onClose={closeModal}
        >
          <ModalSocialCarousel
            cards={cards}
            closeModal={closeModal}
            initialModalCardIndex={initialModalCardIndex ?? 0}
          />
        </Modal>
      </ThemeProvider>
    </Fragment>
  ) : (
    <Spinner sx={{ display: 'block', m: ['100px auto', '160px auto'] }} />
  );
};

type ShareYourExperienceBlockProps = ShareYourExperienceCarouselProps &
  ShareYourExperienceBlockHeaderProps &
  Omit<ComponentPropsWithoutRef<typeof Section>, 'children'> & {
    flexSx?: SxStyleProp;
  };

const ShareYourExperienceBlock = ({
  title,
  feedName: feed,
  hashTags,
  bg = 'background',
  flexSx = {},
  desktopCardsPaddingLeft,
  mobileContainerFullWidth,
  carouselVariant,
  ...props
}: ShareYourExperienceBlockProps): React.JSX.Element => (
  <Section data-section="ShareYourExperience" {...props}>
    <Flex
      sx={{
        flexDirection: 'column',
        position: 'relative',
        bg,
        color: 'text',
        pt: [6, 7],
        pb: [3, 7],
        ...flexSx,
      }}
    >
      <ShareYourExperienceBlockHeader hashTags={hashTags} title={title} />
      <ErrorBoundary FallbackComponent={ErrorMessage}>
        <ShareYourExperienceCarousel
          carouselVariant={carouselVariant}
          desktopCardsPaddingLeft={desktopCardsPaddingLeft}
          feedName={feed}
          mobileContainerFullWidth={mobileContainerFullWidth}
        />
      </ErrorBoundary>
    </Flex>
  </Section>
);

export default ShareYourExperienceBlock;
